import { angleTo, clamp, distance, normalizeAngle, pointInCircle, type Point } from '../core/math';
import { Prng } from '../core/prng';
import {
  type Formation,
  type MemberHealth,
  moraleFromSuppression,
  type MoraleState,
} from '../core/squad';
import { aiDifficulty, type Difficulty } from '../ai/difficulty';
import { abilities, buildings, getAbility, getBuilding, getUnit, sectors } from '../data/content';
import type { Faction, GameMode, UnitCategory } from '../data/types';

export type Owner = Faction | 'neutral';
export type EntityKind = 'unit' | 'building' | 'wreck';
export interface Entity {
  id: number;
  kind: EntityKind;
  owner: Owner;
  definitionId: string;
  name: string;
  category: UnitCategory | 'building';
  position: Point;
  previous: Point;
  target?: Point | undefined;
  waypoints?: Point[] | undefined;
  targetEntity?: number | undefined;
  health: number;
  maxHealth: number;
  speed: number;
  range: number;
  damage: number;
  armor: number;
  facing: number;
  turretFacing?: number | undefined;
  reload: number;
  members?: number | undefined;
  maxMembers?: number | undefined;
  suppression?: number | undefined;
  capacity: number;
  air: boolean;
  antiAir: boolean;
  selected?: boolean | undefined;
  retreating?: boolean | undefined;
  arriving?: boolean | undefined;
  construction?: number | undefined;
  queue?: ReinforcementRequest[] | undefined;
  rally?: Point | undefined;
  order: string;
  veterancy: number;
  critical?: 'engine' | 'weapon' | 'optics' | undefined;
  landed?: boolean | undefined;
  landingTarget?: Point | undefined;
  landingRemaining?: number | undefined;
  deployed?: boolean | undefined;
  passengers?: number[] | undefined;
  transportSlots?: number | undefined;
  embarkedIn?: number | undefined;
  boardingTransportId?: number | undefined;
  boardingRemaining?: number | undefined;
  disembarkTarget?: Point | undefined;
  disembarkRemaining?: number | undefined;
  garrisonedIn?: number | undefined;
  garrisoningBuildingId?: number | undefined;
  garrisonRemaining?: number | undefined;
  garrisonSlots?: number | undefined;
  occupants?: number[] | undefined;
  exitTarget?: Point | undefined;
  exitRemaining?: number | undefined;
  fortification?: boolean | undefined;
  mine?: boolean | undefined;
  concealed?: boolean | undefined;
  wire?: boolean | undefined;
  shield?: number | undefined;
  cover?: 'none' | 'light' | 'heavy' | undefined;
  coverFacing?: number | undefined;
  visible?: boolean | undefined;
  cooldowns?: Record<string, number> | undefined;
  experience: number;
  reinforceProgress?: number | undefined;
  repairTarget?: number | undefined;
  evasiveUntil?: number | undefined;
  relocationUntil?: number | undefined;
  commandedUntil?: number | undefined;
  ambushReady?: boolean | undefined;
  countermeasuresUntil?: number | undefined;
  ammunition?: 'ap' | 'he' | undefined;
  holdFire?: boolean | undefined;
  sightBonus?: number | undefined;
  destroyedOwner?: Faction | undefined;
  formation?: Formation | undefined;
  morale?: MoraleState | undefined;
  memberHealth?: MemberHealth | undefined;
  /** Guided-weapon lock: target entity ID being locked onto. */
  lockTarget?: number | undefined;
  /** Accumulated lock time in seconds; must reach ability.lockTime to fire. */
  lockProgress?: number | undefined;
  /** Seconds remaining before artillery/ATGM setup completes. */
  setupRemaining?: number | undefined;
  /** Whether this is an EW jammer effect that increases lock time and disables auras. */
  ewSource?: boolean | undefined;
  /** Coalition Rifle mutually-exclusive weapon package: 'lmg' or 'at_launcher'. */
  weaponPackage?: 'lmg' | 'at_launcher' | undefined;
}

export interface AreaEffect {
  id: number;
  owner: Faction;
  kind: 'smoke' | 'damage' | 'recon' | 'headwind';
  position: Point;
  radius: number;
  remaining: number;
  damage: number;
  armed: boolean;
  label: string;
}

export interface SectorState {
  id: string;
  owner: Owner;
  progress: number;
  contested: boolean;
  connected: Record<Faction, boolean>;
}
export interface ReinforcementRequest {
  id: number;
  unitId: string;
  elapsed: number;
  active: boolean;
  reserved: boolean;
}
export interface PlayerState {
  faction: Faction;
  supply: number;
  tickets: number;
  capacity: number;
  entities: number;
  depots: number;
  cutoffs: number;
  reservedCapacity: number;
  reservedEntities: number;
}
export interface GameSnapshot {
  entities: Entity[];
  sectors: SectorState[];
  players: Record<Faction, PlayerState>;
  time: number;
  mode: GameMode;
  winner?: Faction | undefined;
  messages: string[];
  effects: AreaEffect[];
  performance: { visibilityMs: number; aiMs: number };
}

export const TICK_SECONDS = 0.05;
const WORLD = { width: 4096, height: 1600 };
/** Centralized balance data — values, not magic numbers in conditionals. */
export const BALANCE = {
  economy: {
    startingSupply: 500,
    baseIncome: 1.25,
    depotIncome: 0.75,
    ticketDrainInterval: 3,
    startingTickets: 500,
  },
  suppression: {
    suppressedThreshold: 60,
    pinnedThreshold: 100,
    suppressedSpeedMultiplier: 0.55,
    pinnedSpeedMultiplier: 0,
    suppressedAccuracyMultiplier: 0.55,
  },
  capture: {
    radius: 80,
    neutralSeconds: 10,
    enemyNeutralizeSeconds: 8,
    progressPerSecond: 3,
    neutralizePerSecond: 12.5,
    reconnaissanceMultiplier: 0.65,
    stackDiminishingFactor: 0.75,
    maxStackedStrength: 1.5,
  },
  recovery: {
    wreckSupplyCost: 90,
    salvageReturn: 45,
    channelSeconds: 4,
  },
  capacity: {
    commandMax: 120,
    entityMax: 40,
    queueMax: 5,
  },
} as const;
const VETERANCY = {
  accuracyPerLevel: 0.04,
  capturePerLevel: 0.08,
  infantryDamageReductionPerLevel: 0.05,
  suppressionRecoveryPerLevel: 1.25,
  reconnaissanceCaptureMultiplier: 0.65,
};
const hqId = (faction: Faction): string => (faction === 'equestria' ? 'eq_hq' : 'id_hq');
const enemy = (faction: Faction): Faction => (faction === 'equestria' ? 'interdef' : 'equestria');

export class GameSimulation {
  private readonly rng: Prng;
  private nextId = 1;
  private nextRequest = 1;
  private nextEffect = 1;
  private readonly entities = new Map<number, Entity>();
  private readonly sectorState = new Map<string, SectorState>();
  private readonly players: Record<Faction, PlayerState>;
  private readonly messages: string[] = [];
  private readonly effects = new Map<number, AreaEffect>();
  private ticketAccumulator = 0;
  private aiAccumulator = 0;
  private visibilityAccumulator = 0;
  private _winner?: Faction;
  private performance = { visibilityMs: 0, aiMs: 0 };
  public time = 0;

  public constructor(
    public readonly playerFaction: Faction,
    public readonly mode: GameMode,
    seed: number,
    public readonly difficulty: Difficulty = 'normal',
  ) {
    this.rng = new Prng(seed);
    this.players = {
      equestria: {
        faction: 'equestria',
        supply: BALANCE.economy.startingSupply,
        tickets: BALANCE.economy.startingTickets,
        capacity: 0,
        entities: 0,
        depots: 0,
        cutoffs: 0,
        reservedCapacity: 0,
        reservedEntities: 0,
      },
      interdef: {
        faction: 'interdef',
        supply: BALANCE.economy.startingSupply,
        tickets: BALANCE.economy.startingTickets,
        capacity: 0,
        entities: 0,
        depots: 0,
        cutoffs: 0,
        reservedCapacity: 0,
        reservedEntities: 0,
      },
    };
    for (const sector of sectors)
      this.sectorState.set(sector.id, {
        id: sector.id,
        owner: sector.initialOwner ?? 'neutral',
        progress: sector.initialOwner ? 100 : 0,
        contested: false,
        connected: { equestria: false, interdef: false },
      });
    this.createInitialForces();
    this.recalculateConnections();
  }

  public snapshot(): GameSnapshot {
    return {
      entities: [...this.entities.values()],
      sectors: [...this.sectorState.values()],
      players: this.players,
      time: this.time,
      mode: this.mode,
      winner: this._winner,
      messages: [...this.messages].slice(-5),
      effects: [...this.effects.values()],
      performance: { ...this.performance },
    };
  }
  public get winner(): Faction | undefined {
    return this._winner;
  }
  public getEntity(id: number): Entity | undefined {
    return this.entities.get(id);
  }
  public allEntities(): Entity[] {
    return [...this.entities.values()];
  }
  public canFactionSee(faction: Faction, entityId: number): boolean {
    const target = this.entities.get(entityId);
    return target ? this.isVisibleToFaction(faction, target) : false;
  }

  private createInitialForces(): void {
    this.spawnBuilding('equestria', 'eq_hq', { x: 3740, y: 760 });
    this.spawnBuilding('interdef', 'id_hq', { x: 350, y: 760 });
    this.spawnUnit('equestria', 'eq_rifle_section', { x: 3520, y: 690 });
    this.spawnUnit('equestria', 'eq_combat_engineers', { x: 3500, y: 830 });
    this.spawnUnit('interdef', 'id_coalition_rifle', { x: 560, y: 690 });
    this.spawnUnit('interdef', 'id_combat_engineers', { x: 580, y: 830 });
    this.spawnGarrison('Rail Yard Offices', { x: 1410, y: 390 }, 1);
    this.spawnGarrison('Industrial Works', { x: 2020, y: 670 }, 2);
    this.spawnGarrison('Residential Block', { x: 1880, y: 1260 }, 1);
  }

  private createEntity(entity: Omit<Entity, 'id'>): Entity {
    const created = { ...entity, id: this.nextId++ };
    this.entities.set(created.id, created);
    if (created.kind === 'unit' && created.owner !== 'neutral') {
      const player = this.players[created.owner];
      player.entities += 1;
      player.capacity += created.capacity;
    }
    return created;
  }
  public spawnUnit(
    owner: Faction,
    definitionId: string,
    position: Point,
    arriving = false,
  ): Entity | undefined {
    const definition = getUnit(definitionId);
    if (!definition) return undefined;
    return this.createEntity({
      kind: 'unit',
      owner,
      definitionId,
      name: definition.name,
      category: definition.category,
      position: { ...position },
      previous: { ...position },
      health: definition.health,
      maxHealth: definition.health,
      speed: definition.speed,
      range: definition.range,
      damage: definition.damage,
      armor: definition.armor,
      facing: owner === 'equestria' ? Math.PI : 0,
      turretFacing:
        definition.category === 'vehicle' ? (owner === 'equestria' ? Math.PI : 0) : undefined,
      reload: 0,
      members: definition.members,
      maxMembers: definition.members,
      suppression: definition.members ? 0 : undefined,
      formation: definition.members ? 'loose' : undefined,
      morale: definition.members ? 'normal' : undefined,
      memberHealth: definition.members
        ? {
            fractions: Array.from({ length: definition.members }, () => 1),
            specialistIndex: this.specialistMemberIndex(definitionId),
          }
        : undefined,
      capacity: definition.capacity,
      air: definition.air ?? false,
      antiAir: definition.antiAir ?? false,
      arriving,
      order: arriving ? 'Arriving' : 'Idle',
      veterancy: 0,
      experience: 0,
      cover: 'none',
      visible: owner === this.playerFaction,
      transportSlots: this.transportSlots(definitionId),
      passengers: [],
      cooldowns: {},
    });
  }
  private spawnBuilding(
    owner: Faction,
    definitionId: string,
    position: Point,
    construction?: number,
  ): Entity {
    const definition = getBuilding(definitionId);
    const isHeadquarters = definitionId === hqId(owner);
    return this.createEntity({
      kind: 'building',
      owner,
      definitionId,
      name: isHeadquarters ? 'Field Headquarters' : (definition?.name ?? definitionId),
      category: 'building',
      position: { ...position },
      previous: { ...position },
      health: isHeadquarters ? 1800 : 900,
      maxHealth: isHeadquarters ? 1800 : 900,
      speed: 0,
      range: isHeadquarters ? 190 : 0,
      damage: isHeadquarters ? 14 : 0,
      armor: isHeadquarters ? 80 : 35,
      facing: 0,
      reload: 0,
      capacity: 0,
      air: false,
      antiAir: false,
      construction,
      queue: [],
      rally: { x: position.x + (owner === 'equestria' ? -150 : 150), y: position.y },
      order: construction ? 'Constructing' : 'Operational',
      veterancy: 0,
      experience: 0,
      cover: 'none',
      visible: owner === this.playerFaction,
      cooldowns: {},
    });
  }
  private spawnGarrison(name: string, position: Point, slots: number): Entity {
    return this.createEntity({
      kind: 'building',
      owner: 'neutral',
      definitionId: `garrison_${this.nextId}`,
      name,
      category: 'building',
      position: { ...position },
      previous: { ...position },
      health: 700,
      maxHealth: 700,
      speed: 0,
      range: 0,
      damage: 0,
      armor: 25,
      facing: 0,
      reload: 0,
      capacity: 0,
      air: false,
      antiAir: false,
      queue: [],
      rally: { ...position },
      order: 'Garrison building',
      veterancy: 0,
      experience: 0,
      cooldowns: {},
      cover: 'heavy',
      visible: true,
      garrisonSlots: slots,
      occupants: [],
    });
  }

  private specialistMemberIndex(definitionId: string): number | undefined {
    // Special-weapon members (AT launcher, HMG, designator) occupy the highest
    // surviving index so ordinary members deplete first.
    const definition = getUnit(definitionId);
    const count = definition?.members;
    if (!count) return undefined;
    if (
      definitionId.includes('javelin') ||
      definitionId.includes('stinger') ||
      definitionId.includes('lancer') ||
      definitionId.includes('m2hb') ||
      definitionId.includes('fgm') ||
      definitionId.includes('fim')
    )
      return count - 1;
    return undefined;
  }

  private updateMorale(entity: Entity): void {
    if (entity.suppression === undefined) return;
    entity.morale = moraleFromSuppression(entity.suppression, Boolean(entity.retreating));
  }

  /**
   * Apply damage to per-member health. Selects a surviving member, removes it
   * only when its fractional HP reaches zero, and preserves specialist members
   * (e.g. AT/HMG gunners) until ordinary members are depleted where practical.
   */
  private applySquadDamage(entity: Entity, rawDamage: number): void {
    if (!entity.memberHealth || !entity.maxMembers) return;
    const mh = entity.memberHealth;
    if (!mh.fractions.length) return;
    const totalDamage = rawDamage / entity.maxHealth;
    const specialist = mh.specialistIndex;
    // Candidate targets: ordinary members first; specialist only when alone.
    const ordinaryAlive = mh.fractions
      .map((fraction, index) => ({ fraction, index }))
      .filter((entry) => entry.index !== specialist && entry.fraction > 0);
    const targetEntry =
      ordinaryAlive.length > 0
        ? ordinaryAlive[ordinaryAlive.length - 1]
        : mh.fractions
            .map((fraction, index) => ({ fraction, index }))
            .find((entry) => entry.fraction > 0);
    if (!targetEntry) return;
    const before = targetEntry.fraction;
    targetEntry.fraction = Math.max(0, targetEntry.fraction - totalDamage);
    const removed = before > 0 && targetEntry.fraction <= 0;
    if (removed) {
      // Rebuild surviving fractions, dropping the dead member.
      mh.fractions = mh.fractions
        .map((fraction, index) => (index === targetEntry.index ? -1 : fraction))
        .filter((fraction) => fraction >= 0);
      if (specialist !== undefined && targetEntry.index < specialist)
        mh.specialistIndex = Math.max(0, specialist - 1);
      else if (specialist === targetEntry.index) mh.specialistIndex = undefined;
      if (entity.members !== undefined) entity.members = Math.max(0, entity.members - 1);
    } else {
      mh.fractions[targetEntry.index] = targetEntry.fraction;
    }
  }

  public tick(): void {
    if (this._winner) return;
    this.time += TICK_SECONDS;
    this.runQueues();
    this.runConstruction();
    this.runLandingTransitions();
    this.runTransportTransitions();
    this.runGarrisonTransitions();
    this.runGuidedLocks();
    this.runSetup();
    this.runMovement();
    this.runMines();
    this.runCapture();
    this.visibilityAccumulator += TICK_SECONDS;
    if (this.visibilityAccumulator >= 0.1) {
      this.visibilityAccumulator = 0;
      const started = performance.now();
      this.runVisibility();
      this.performance.visibilityMs = performance.now() - started;
    }
    this.runEffects();
    this.runCombat();
    this.runHealing();
    this.runEconomy();
    this.runVictory();
    this.aiAccumulator += TICK_SECONDS;
    if (this.aiAccumulator >= aiDifficulty[this.difficulty].decisionInterval) {
      this.aiAccumulator = 0;
      const started = performance.now();
      this.runAi();
      this.performance.aiMs = performance.now() - started;
    }
  }

  public select(ids: number[]): void {
    for (const entity of this.entities.values()) entity.selected = ids.includes(entity.id);
  }
  public issueMove(ids: number[], target: Point, attackMove = false, reverse = false): void {
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (!entity || entity.owner !== this.playerFaction || entity.retreating) continue;
      entity.target = this.clampPoint(target);
      entity.waypoints = undefined;
      entity.targetEntity = undefined;
      entity.order = reverse ? 'Reverse move' : attackMove ? 'Attack move' : 'Move';
      if (reverse) entity.facing = angleTo(target, entity.position);
    }
  }
  public applyPath(entityId: number, goal: Point, waypoints: Point[]): void {
    const entity = this.entities.get(entityId);
    if (!entity || !entity.target || distance(entity.target, goal) > 2) return;
    entity.waypoints = waypoints.map((point) => this.clampPoint(point));
  }
  public issueAttack(ids: number[], targetId: number): void {
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (!entity || entity.owner !== this.playerFaction) continue;
      entity.targetEntity = targetId;
      entity.target = undefined;
      entity.order = 'Attack';
    }
  }
  public stop(ids: number[]): void {
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (!entity || entity.owner !== this.playerFaction) continue;
      entity.target = undefined;
      entity.waypoints = undefined;
      entity.targetEntity = undefined;
      entity.order = 'Stop';
    }
  }
  public hold(ids: number[]): void {
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (!entity || entity.owner !== this.playerFaction) continue;
      entity.target = undefined;
      entity.waypoints = undefined;
      entity.order = 'Hold position';
    }
  }
  public face(ids: number[], point: Point): void {
    for (const id of ids) {
      const entity = this.entities.get(id);
      if (!entity || entity.owner !== this.playerFaction || entity.kind === 'wreck') continue;
      entity.facing = angleTo(entity.position, point);
      entity.target = undefined;
      entity.waypoints = undefined;
      entity.order = 'Facing position';
    }
  }
  public groundAttack(ids: number[], point: Point): string | undefined {
    const attacker = ids
      .map((id) => this.entities.get(id))
      .find((entity) => {
        const definition = entity ? getUnit(entity.definitionId) : undefined;
        return (
          entity?.owner === this.playerFaction &&
          entity.kind === 'unit' &&
          definition?.abilities.some((ability) => /barrage|rocket|grenade|volley/i.test(ability))
        );
      });
    if (!attacker) return 'Select a unit with an indirect or area-fire weapon.';
    this.addEffect(
      this.playerFaction,
      'damage',
      point,
      90,
      1,
      attacker.damage,
      `${attacker.name} ground attack`,
    );
    attacker.order = 'Ground attack';
    attacker.reload = Math.max(attacker.reload, 2);
    return undefined;
  }
  public retreat(ids: number[]): void {
    const hq = this.findHeadquarters(this.playerFaction);
    if (!hq) return;
    for (const id of ids) {
      const unit = this.entities.get(id);
      if (unit?.owner === this.playerFaction && unit.category === 'infantry') {
        unit.retreating = true;
        unit.formation = 'retreat';
        unit.target = { ...hq.position };
        unit.order = 'Retreating';
      }
    }
  }
  public placeBuilding(buildingId: string, position: Point): string | undefined {
    const definition = getBuilding(buildingId);
    const owner = this.playerFaction;
    if (!definition || definition.faction !== owner) return 'Invalid command building.';
    const engineer = [...this.entities.values()].find(
      (entity) =>
        entity.owner === owner &&
        entity.selected &&
        entity.definitionId.includes('engineer') &&
        !entity.embarkedIn,
    );
    if (!engineer) return 'Select a combat engineer to construct this building.';
    if (!this.hasPrerequisites(owner, definition.prerequisites)) return 'Prerequisite missing.';
    if (this.players[owner].supply < definition.cost) return 'Insufficient Supply.';
    const base = this.findSector(owner, 'base');
    if (!base || distance(base.point, position) > 420)
      return 'Buildings must be inside the base construction zone.';
    if (
      [...this.entities.values()].some(
        (e) => e.kind === 'building' && distance(e.position, position) < 120,
      )
    )
      return 'Building footprint blocked.';
    this.players[owner].supply -= definition.cost;
    engineer.target = this.clampPoint(position);
    engineer.order = `Constructing ${definition.name}`;
    this.spawnBuilding(owner, buildingId, this.clampPoint(position), definition.buildTime);
    this.message(`${definition.name} construction started.`);
    return undefined;
  }
  public buildFortification(position: Point): string | undefined {
    const engineer = [...this.entities.values()].find(
      (entity) =>
        entity.owner === this.playerFaction &&
        entity.selected &&
        entity.definitionId.includes('engineer') &&
        !entity.embarkedIn &&
        !entity.garrisonedIn,
    );
    if (!engineer) return 'Select a combat engineer first.';
    const sector = sectors.find((entry) => distance(entry.point, position) < 240);
    const state = sector ? this.sectorState.get(sector.id) : undefined;
    if (!state || state.owner !== this.playerFaction || !state.connected[this.playerFaction])
      return 'Fortifications require connected friendly territory.';
    if (this.players[this.playerFaction].supply < 35) return 'Insufficient Supply.';
    this.players[this.playerFaction].supply -= 35;
    this.createEntity({
      kind: 'building',
      owner: this.playerFaction,
      definitionId: `sandbag_${this.nextId}`,
      name: 'Sandbag wall',
      category: 'building',
      position: this.clampPoint(position),
      previous: this.clampPoint(position),
      health: 360,
      maxHealth: 360,
      speed: 0,
      range: 0,
      damage: 0,
      armor: 15,
      facing: 0,
      reload: 0,
      capacity: 0,
      air: false,
      antiAir: false,
      queue: [],
      rally: this.clampPoint(position),
      order: 'Fortification',
      veterancy: 0,
      experience: 0,
      cooldowns: {},
      cover: 'heavy',
      visible: true,
      fortification: true,
    });
    this.message('Sandbag wall constructed.');
    return undefined;
  }
  public buildMine(position: Point): string | undefined {
    const engineer = [...this.entities.values()].find(
      (entity) =>
        entity.owner === this.playerFaction &&
        entity.selected &&
        entity.definitionId.includes('engineer') &&
        !entity.embarkedIn &&
        !entity.garrisonedIn,
    );
    if (!engineer) return 'Select a combat engineer first.';
    const sector = sectors.find((entry) => distance(entry.point, position) < 240);
    const state = sector ? this.sectorState.get(sector.id) : undefined;
    if (!state || state.owner !== this.playerFaction || !state.connected[this.playerFaction])
      return 'Mines require connected friendly territory.';
    if (this.players[this.playerFaction].supply < 25) return 'Insufficient Supply.';
    this.players[this.playerFaction].supply -= 25;
    this.createEntity({
      kind: 'building',
      owner: this.playerFaction,
      definitionId: `mine_${this.nextId}`,
      name: 'Anti-vehicle mine',
      category: 'building',
      position: this.clampPoint(position),
      previous: this.clampPoint(position),
      health: 1,
      maxHealth: 1,
      speed: 0,
      range: 0,
      damage: 0,
      armor: 0,
      facing: 0,
      reload: 0,
      capacity: 0,
      air: false,
      antiAir: false,
      queue: [],
      rally: this.clampPoint(position),
      order: 'Concealed mine',
      veterancy: 0,
      experience: 0,
      cooldowns: {},
      cover: 'none',
      visible: true,
      mine: true,
      concealed: true,
    });
    this.message('Anti-vehicle mine deployed.');
    return undefined;
  }
  public buildFightingPosition(position: Point): string | undefined {
    const engineer = [...this.entities.values()].find(
      (entity) =>
        entity.owner === this.playerFaction &&
        entity.selected &&
        entity.definitionId.includes('engineer'),
    );
    if (!engineer) return 'Select a combat engineer first.';
    const sector = sectors.find((entry) => distance(entry.point, position) < 240);
    const state = sector ? this.sectorState.get(sector.id) : undefined;
    if (!state || state.owner !== this.playerFaction || !state.connected[this.playerFaction])
      return 'Fighting positions require connected friendly territory.';
    if (this.players[this.playerFaction].supply < 50) return 'Insufficient Supply.';
    this.players[this.playerFaction].supply -= 50;
    const point = this.clampPoint(position);
    this.createEntity({
      kind: 'building',
      owner: this.playerFaction,
      definitionId: `fighting_position_${this.nextId}`,
      name: 'Fighting position',
      category: 'building',
      position: point,
      previous: point,
      health: 480,
      maxHealth: 480,
      speed: 0,
      range: 0,
      damage: 0,
      armor: 20,
      facing: 0,
      reload: 0,
      capacity: 0,
      air: false,
      antiAir: false,
      queue: [],
      rally: point,
      order: 'Fighting position',
      veterancy: 0,
      experience: 0,
      cooldowns: {},
      cover: 'heavy',
      visible: true,
      fortification: true,
      garrisonSlots: 1,
      occupants: [],
    });
    this.message('Fighting position constructed.');
    return undefined;
  }
  public buildWire(position: Point): string | undefined {
    const engineer = [...this.entities.values()].find(
      (entity) =>
        entity.owner === this.playerFaction &&
        entity.selected &&
        entity.definitionId.includes('engineer'),
    );
    if (!engineer) return 'Select a combat engineer first.';
    const sector = sectors.find((entry) => distance(entry.point, position) < 240);
    const state = sector ? this.sectorState.get(sector.id) : undefined;
    if (!state || state.owner !== this.playerFaction || !state.connected[this.playerFaction])
      return 'Barbed wire requires connected friendly territory.';
    if (this.players[this.playerFaction].supply < 20) return 'Insufficient Supply.';
    this.players[this.playerFaction].supply -= 20;
    const point = this.clampPoint(position);
    this.createEntity({
      kind: 'building',
      owner: this.playerFaction,
      definitionId: `wire_${this.nextId}`,
      name: 'Barbed wire',
      category: 'building',
      position: point,
      previous: point,
      health: 120,
      maxHealth: 120,
      speed: 0,
      range: 0,
      damage: 0,
      armor: 0,
      facing: 0,
      reload: 0,
      capacity: 0,
      air: false,
      antiAir: false,
      queue: [],
      rally: point,
      order: 'Barbed wire',
      veterancy: 0,
      experience: 0,
      cooldowns: {},
      cover: 'none',
      visible: true,
      fortification: true,
      wire: true,
    });
    this.message('Barbed wire constructed.');
    return undefined;
  }
  private clearEngineeringTarget(
    target: Point,
    property: 'mine' | 'wire',
    label: string,
  ): string | undefined {
    const engineer = [...this.entities.values()].find(
      (entity) =>
        entity.owner === this.playerFaction &&
        entity.selected &&
        entity.definitionId.includes('engineer'),
    );
    if (!engineer) return 'Select a combat engineer first.';
    const obstacle = [...this.entities.values()].find(
      (entity) => entity[property] && distance(entity.position, target) < 70,
    );
    if (!obstacle) return `No ${label} is within clearing range.`;
    this.entities.delete(obstacle.id);
    engineer.order = `Clearing ${label}`;
    this.message(`${label[0]?.toUpperCase()}${label.slice(1)} cleared.`);
    return undefined;
  }
  public requestReinforcement(buildingEntityId: number, unitId: string): string | undefined {
    const building = this.entities.get(buildingEntityId);
    const definition = getUnit(unitId);
    if (
      !building ||
      building.owner !== this.playerFaction ||
      building.kind !== 'building' ||
      building.construction ||
      !definition ||
      definition.faction !== this.playerFaction
    )
      return 'Invalid reinforcement request.';
    if (!building.queue || building.queue.length >= 5) return 'Deployment queue full.';
    const headquartersBasic =
      building.definitionId === hqId(this.playerFaction) &&
      (definition.id.includes('rifle') || definition.id.includes('engineer'));
    if (definition.source !== building.definitionId && !headquartersBasic)
      return 'This building cannot request that unit.';
    const player = this.players[this.playerFaction];
    if (player.supply < definition.cost) return 'Insufficient Supply.';
    if (player.entities + player.reservedEntities >= BALANCE.capacity.entityMax)
      return 'Entity cap reached.';
    if (
      player.capacity + player.reservedCapacity + definition.capacity >
      BALANCE.capacity.commandMax
    )
      return 'Command Capacity exceeded.';
    player.supply -= definition.cost;
    building.queue.push({
      id: this.nextRequest++,
      unitId,
      elapsed: 0,
      active: building.queue.length === 0,
      reserved: false,
    });
    this.message(`${definition.name} requested.`);
    return undefined;
  }
  public cancelRequest(buildingId: number, requestId: number): void {
    const building = this.entities.get(buildingId);
    if (!building?.queue || building.owner !== this.playerFaction) return;
    const index = building.queue.findIndex((request) => request.id === requestId);
    const request = building.queue[index];
    if (index < 0 || !request) return;
    const definition = getUnit(request.unitId);
    if (!definition) return;
    const refund = request.active
      ? request.elapsed / definition.requestTime < 0.5
        ? 0.75
        : 0.5
      : 1;
    this.players[this.playerFaction].supply += definition.cost * refund;
    if (request.reserved) {
      this.players[this.playerFaction].reservedCapacity -= definition.capacity;
      this.players[this.playerFaction].reservedEntities -= 1;
    }
    building.queue.splice(index, 1);
    if (building.queue[0]) building.queue[0].active = true;
  }
  public setRally(buildingId: number, position: Point): void {
    const building = this.entities.get(buildingId);
    if (building?.owner === this.playerFaction && building.kind === 'building')
      building.rally = this.clampPoint(position);
  }
  public repair(ids: number[], patientId: number): string | undefined {
    const repairer = ids
      .map((id) => this.entities.get(id))
      .find(
        (entity) =>
          entity?.owner === this.playerFaction &&
          entity.kind === 'unit' &&
          (entity.definitionId.includes('engineer') || entity.definitionId.includes('m88a2')),
      );
    const patient = this.entities.get(patientId);
    if (!repairer) return 'Select a combat engineer or recovery vehicle.';
    if (
      !patient ||
      patient.owner !== this.playerFaction ||
      patient.kind !== 'unit' ||
      patient.category !== 'vehicle'
    )
      return 'Repair requires a friendly damaged vehicle.';
    if (patient.health >= patient.maxHealth && !patient.critical)
      return 'Vehicle does not need repair.';
    repairer.repairTarget = patient.id;
    repairer.target = { ...patient.position };
    repairer.order = `Repairing ${patient.name}`;
    return undefined;
  }

  public activateAbility(ids: number[], abilityId: string, target: Point): string | undefined {
    const unit = ids
      .map((id) => this.entities.get(id))
      .find(
        (entity) =>
          entity?.owner === this.playerFaction &&
          entity.kind === 'unit' &&
          !entity.embarkedIn &&
          !entity.boardingTransportId,
      );
    if (!unit) return 'Select a friendly unit first.';
    const definition = getUnit(unit.definitionId);
    if (!definition || !definition.abilities.some((name) => this.slug(name) === abilityId))
      return 'That unit cannot use this ability.';
    const cooldown = unit.cooldowns?.[abilityId] ?? 0;
    if (cooldown > 0) return `Ability recharging (${Math.ceil(cooldown)}s).`;
    const normalized = abilityId.toLowerCase();
    const isArtillery = /sh32|paladin|howitzer|m109/.test(unit.definitionId);
    if (normalized.includes('fighting_position')) return this.buildFightingPosition(target);
    if (normalized.includes('barbed_wire')) return this.buildWire(target);
    if (normalized.includes('clear_mines'))
      return this.clearEngineeringTarget(target, 'mine', 'mine');
    if (normalized.includes('demolish_obstacles'))
      return this.clearEngineeringTarget(target, 'wire', 'barbed wire');
    if (normalized.includes('construct')) return this.buildFortification(target);
    if (normalized.includes('mine')) return this.buildMine(target);
    if (normalized.includes('evasive_break')) {
      unit.evasiveUntil = this.time + 6;
      unit.order = 'Evasive break';
      this.setCooldown(unit, abilityId, 28);
      return undefined;
    }
    if (normalized.includes('rapid_relocation')) {
      unit.relocationUntil = this.time + 7;
      unit.order = 'Rapid relocation';
      this.setCooldown(unit, abilityId, 28);
      return undefined;
    }
    if (normalized.includes('prepared_ambush')) {
      unit.deployed = !unit.deployed;
      unit.ambushReady = unit.deployed;
      unit.target = undefined;
      unit.waypoints = undefined;
      unit.order = unit.deployed ? 'Prepared ambush' : 'Ambush cancelled';
      this.setCooldown(unit, abilityId, 12);
      return undefined;
    }
    if (normalized.includes('countermeasure')) {
      unit.countermeasuresUntil = this.time + 6;
      unit.order = 'Countermeasures active';
      this.setCooldown(unit, abilityId, 24);
      return undefined;
    }
    if (normalized.includes('command_aura')) {
      let affected = 0;
      for (const ally of this.entities.values())
        if (
          ally.owner === unit.owner &&
          ally.kind === 'unit' &&
          !ally.embarkedIn &&
          distance(ally.position, unit.position) <= 180
        ) {
          ally.commandedUntil = this.time + 10;
          affected++;
        }
      unit.order = `Command aura (${affected} units)`;
      this.setCooldown(unit, abilityId, 24);
      return undefined;
    }
    if (normalized.includes('ap_he_ammunition')) {
      unit.ammunition = unit.ammunition === 'he' ? 'ap' : 'he';
      unit.reload = Math.max(unit.reload, 2);
      unit.order = `${unit.ammunition.toUpperCase()} ammunition selected`;
      this.setCooldown(unit, abilityId, 4);
      return undefined;
    }
    // Coalition Rifle mutually-exclusive upgrade: LMG or AT launcher.
    if (normalized.includes('lmg_package') || normalized.includes('at_package')) {
      if (unit.weaponPackage) return 'Rifle squad already has a weapon package.';
      const pkg = normalized.includes('lmg_package') ? 'lmg' : 'at_launcher';
      unit.weaponPackage = pkg;
      unit.damage = pkg === 'lmg' ? 22 : (unit.damage ?? 0) + 0;
      unit.range = pkg === 'at_launcher' ? 280 : unit.range;
      unit.order = `${pkg === 'lmg' ? 'LMG' : 'AT'} package installed`;
      this.setCooldown(unit, abilityId, 18);
      return undefined;
    }
    if (normalized.includes('hold_fire')) {
      unit.holdFire = !unit.holdFire;
      unit.order = unit.holdFire ? 'Hold fire' : 'Weapons free';
      return undefined;
    }
    if (
      normalized.includes('deploy_stabilizers') ||
      normalized.includes('observation_position') ||
      normalized.includes('set_up')
    ) {
      if (isArtillery) {
        unit.setupRemaining = 3;
        unit.target = undefined;
        unit.waypoints = undefined;
        unit.order = 'Setting up artillery';
        this.setCooldown(unit, abilityId, 6);
        return undefined;
      }
      unit.deployed = !unit.deployed;
      unit.target = undefined;
      unit.waypoints = undefined;
      unit.order = unit.deployed ? 'Stabilizers deployed' : 'Stabilizers retracted';
      this.setCooldown(unit, abilityId, 4);
      return undefined;
    }
    if (
      normalized.includes('observation_mode') ||
      normalized.includes('sensor_mast') ||
      normalized.includes('mast_sensor')
    ) {
      unit.deployed = !unit.deployed;
      unit.sightBonus = unit.deployed ? 180 : undefined;
      unit.target = undefined;
      unit.waypoints = undefined;
      unit.order = unit.deployed ? 'Observation mode' : 'Observation mode ended';
      this.setCooldown(unit, abilityId, 5);
      return undefined;
    }
    if (normalized.includes('reinforce') || normalized.includes('deploy_reinforcement_point')) {
      unit.deployed = !unit.deployed;
      unit.order = unit.deployed
        ? 'Forward reinforcement deployed'
        : 'Forward reinforcement packed';
      this.setCooldown(unit, abilityId, 5);
      return undefined;
    }
    if (normalized.includes('deploy_medical')) {
      unit.deployed = !unit.deployed;
      unit.order = unit.deployed ? 'Medical station deployed' : 'Medical station packed';
      this.setCooldown(unit, abilityId, 5);
      return undefined;
    }
    if (normalized.includes('recover_wreck') || normalized.includes('salvage')) {
      const wreck = [...this.entities.values()]
        .filter(
          (entity) =>
            entity.kind === 'wreck' &&
            distance(entity.position, target) <= 110 &&
            entity.owner === 'neutral',
        )
        .sort((a, b) => distance(a.position, target) - distance(b.position, target))[0];
      if (!wreck) return 'No recoverable wreck is nearby.';
      if (normalized.includes('recover_wreck')) {
        if (wreck.destroyedOwner !== unit.owner || !this.isRecoverableWreck(wreck))
          return 'That wreck cannot be recovered.';
        if (this.players[unit.owner as Faction].supply < BALANCE.recovery.wreckSupplyCost)
          return `Recovery requires ${BALANCE.recovery.wreckSupplyCost} Supply.`;
        this.players[unit.owner as Faction].supply -= BALANCE.recovery.wreckSupplyCost;
        this.entities.delete(wreck.id);
        const restored = this.spawnUnit(unit.owner as Faction, wreck.definitionId, wreck.position);
        if (!restored) return 'Wreck recovery failed.';
        restored.health = Math.ceil(restored.maxHealth * 0.3);
        restored.critical = 'engine';
        restored.order = 'Recovered — requires repair';
        unit.order = `Recovering ${restored.name}`;
        this.setCooldown(unit, abilityId, 35);
        this.message(`${unit.name} recovered ${restored.name}; repairs are required.`);
        return undefined;
      }
      this.entities.delete(wreck.id);
      this.players[unit.owner as Faction].supply += BALANCE.recovery.salvageReturn;
      unit.order = 'Salvaging wreck';
      this.setCooldown(unit, abilityId, 20);
      this.message(`${unit.name} recovered ${wreck.name}.`);
      return undefined;
    }
    if (normalized.includes('land') || normalized.includes('takeoff')) {
      if (!unit.air) return 'Only airborne units can land or take off.';
      if (unit.landingRemaining !== undefined || unit.landingTarget)
        return 'Aircraft is already changing state.';
      if (unit.landed) {
        unit.landingRemaining = 1;
        unit.order = 'Taking off';
      } else {
        const landingTarget = this.clampPoint(target);
        if (!this.isClearLandingArea(unit, landingTarget))
          return 'Landing area must be clear of obstacles and units.';
        unit.landingTarget = landingTarget;
        unit.target = { ...landingTarget };
        unit.waypoints = undefined;
        unit.order = 'Approaching landing zone';
      }
      this.setCooldown(unit, abilityId, 8);
      return undefined;
    }
    if (
      normalized.includes('heal') ||
      normalized.includes('repair') ||
      normalized.includes('casualty_evacuation')
    ) {
      const patient = this.closestFriendly(unit.owner as Faction, target, 115);
      if (!patient) return 'No friendly unit in range.';
      patient.health = Math.min(
        patient.maxHealth,
        patient.health + (normalized.includes('casualty_evacuation') ? 110 : 160),
      );
      patient.critical = undefined;
      if (normalized.includes('casualty_evacuation') && patient.suppression !== undefined)
        patient.suppression = Math.max(0, patient.suppression - 35);
      this.setCooldown(unit, abilityId, 18);
      return undefined;
    }
    if (normalized.includes('clear_skies')) {
      let cleared = 0;
      for (const effect of this.effects.values()) {
        if (effect.kind === 'smoke' && distance(effect.position, target) <= effect.radius + 120) {
          this.effects.delete(effect.id);
          cleared++;
        }
      }
      if (!cleared) return 'No smoke or cloud is within the clear-skies area.';
      this.message('Clear Skies dispersed local smoke and cloud cover.');
    } else if (normalized.includes('active_radar') || normalized.includes('active_scan')) {
      this.addEffect(unit.owner as Faction, 'recon', target, 260, 10, 0, 'Active sensor scan');
    } else if (normalized.includes('headwind')) {
      this.addEffect(unit.owner as Faction, 'headwind', target, 180, 12, 0, 'Localized headwind');
    } else if (
      normalized.includes('cloud') ||
      normalized.includes('smoke') ||
      normalized.includes('distortion')
    )
      this.addEffect(unit.owner as Faction, 'smoke', target, 120, 14, 0, 'Smoke / cloud');
    else if (
      normalized.includes('barrage') ||
      normalized.includes('rocket') ||
      normalized.includes('grenade') ||
      normalized.includes('shot') ||
      normalized.includes('focused') ||
      normalized.includes('top_attack') ||
      normalized.includes('hellfire') ||
      normalized.includes('tow') ||
      normalized.includes('sabot') ||
      normalized.includes('volley') ||
      normalized.includes('assault')
    ) {
      const firingAbility = definition.abilities.find((name) => this.slug(name) === abilityId);
      const abilityDef = firingAbility
        ? abilities.find((entry) => this.slug(entry.name) === this.slug(firingAbility))
        : undefined;
      // Artillery minimum range check (180 world units for all tube artillery).
      if (isArtillery && distance(unit.position, target) < 180)
        return 'Minimum range 180 units; target is too close.';
      // Artillery must be deployed (setup completed) to fire.
      if (isArtillery && unit.setupRemaining !== undefined)
        return 'Artillery must complete setup before firing.';
      if (isArtillery && !unit.deployed) {
        unit.setupRemaining = 4;
        unit.target = undefined;
        unit.waypoints = undefined;
        unit.order = 'Setting up artillery';
        unit.deployed = undefined;
        this.setCooldown(unit, abilityId, 6);
        return undefined;
      }
      // Guided-weapon lock: find nearest enemy to lock onto.
      const needsLock = /hellfire|tow|focused_missile|top_attack/.test(normalized);
      const lockCandidate = needsLock
        ? [...this.entities.values()]
            .filter(
              (entry) =>
                entry.owner !== unit.owner &&
                entry.owner !== 'neutral' &&
                entry.health > 0 &&
                distance(entry.position, target) < 80,
            )
            .sort((a, b) => distance(a.position, target) - distance(b.position, target))[0]
        : undefined;
      if (needsLock && !lockCandidate) return 'No valid lock target near that point.';
      if (needsLock && unit.lockTarget !== lockCandidate!.id) {
        unit.lockTarget = lockCandidate!.id;
        unit.lockProgress = 0;
        unit.order = `Acquiring lock on ${lockCandidate!.name}`;
        return undefined;
      }
      if (
        needsLock &&
        unit.lockProgress !== undefined &&
        unit.lockProgress < (abilityDef?.lockTime ?? 2.5)
      )
        return `Locking ${Math.round(((unit.lockProgress ?? 0) / (abilityDef?.lockTime ?? 2.5)) * 100)}% ...`;
      if (needsLock) {
        unit.lockTarget = undefined;
        unit.lockProgress = undefined;
      }
      const owner = unit.owner as Faction;
      const observed = [...this.effects.values()].some(
        (effect) =>
          effect.owner === owner &&
          effect.kind === 'recon' &&
          distance(effect.position, target) <= effect.radius,
      );
      const precision =
        normalized.includes('precision') ||
        normalized.includes('focused') ||
        normalized.includes('top_attack') ||
        normalized.includes('hellfire') ||
        normalized.includes('tow') ||
        normalized.includes('sabot') ||
        observed;
      const scatter = precision ? 18 : 85;
      this.addEffect(
        owner,
        'damage',
        {
          x: target.x + this.rng.range(-scatter, scatter),
          y: target.y + this.rng.range(-scatter, scatter),
        },
        precision ? 55 : 95,
        1.5,
        normalized.includes('barrage') ? 120 : precision ? 110 : 65,
        observed ? 'Observer-guided strike' : 'Tactical strike',
      );
    } else if (normalized.includes('aegis')) {
      const ally = this.closestFriendly(unit.owner as Faction, target, 140);
      if (!ally) return 'Choose a nearby friendly unit.';
      ally.shield = 110;
      this.message(`${ally.name} received Aegis shielding.`);
    } else this.addEffect(unit.owner as Faction, 'recon', target, 180, 8, 0, 'Reconnaissance');
    this.setCooldown(unit, abilityId, normalized.includes('barrage') ? 35 : 22);
    return undefined;
  }

  public activateSupport(abilityId: string, target: Point): string | undefined {
    const ability = getAbility(abilityId);
    if (
      !ability ||
      ability.faction !== this.playerFaction ||
      !this.hasBuilding(this.playerFaction, ability.source)
    )
      return 'Support source is unavailable.';
    const hq = this.findHeadquarters(this.playerFaction);
    if (!hq) return 'Headquarters is destroyed.';
    const cooldown = hq.cooldowns?.[abilityId] ?? 0;
    if (cooldown > 0) return `Support recharging (${Math.ceil(cooldown)}s).`;
    if (this.players[this.playerFaction].supply < ability.supplyCost) return 'Insufficient Supply.';
    this.players[this.playerFaction].supply -= ability.supplyCost;
    const effectKind: AreaEffect['kind'] =
      ability.kind === 'damage' || ability.kind === 'smoke' || ability.kind === 'recon'
        ? ability.kind
        : 'recon';
    this.addEffect(
      this.playerFaction,
      effectKind,
      target,
      ability.radius,
      ability.delay ?? 12,
      ability.damage ?? 0,
      ability.name,
    );
    this.setCooldown(hq, abilityId, ability.cooldown);
    this.message(`${ability.name} confirmed.`);
    return undefined;
  }

  public embark(ids: number[], transportId: number): string | undefined {
    const transport = this.entities.get(transportId);
    if (!transport || transport.owner !== this.playerFaction || !transport.transportSlots)
      return 'Select a friendly transport.';
    const passengers = ids
      .map((id) => this.entities.get(id))
      .filter((entry): entry is Entity =>
        Boolean(
          entry &&
          entry.owner === this.playerFaction &&
          this.isGroundInfantry(entry) &&
          !entry.embarkedIn &&
          !entry.boardingTransportId &&
          !entry.retreating &&
          (entry.suppression ?? 0) < 100 &&
          distance(entry.position, transport.position) < 90,
        ),
      );
    if (!passengers.length) return 'Move infantry next to the transport first.';
    for (const passenger of passengers) {
      if ((transport.passengers?.length ?? 0) >= transport.transportSlots) break;
      passenger.boardingTransportId = transport.id;
      passenger.boardingRemaining = 1;
      passenger.target = undefined;
      passenger.order = 'Boarding transport';
    }
    return undefined;
  }

  public disembark(transportId: number, target: Point): string | undefined {
    const transport = this.entities.get(transportId);
    if (!transport || transport.owner !== this.playerFaction || !transport.passengers?.length)
      return 'Transport has no passengers.';
    if (transport.disembarkRemaining !== undefined) return 'Transport is already disembarking.';
    transport.disembarkTarget = this.clampPoint(target);
    transport.disembarkRemaining = 1;
    transport.order = 'Disembarking passengers';
    return undefined;
  }
  public garrison(ids: number[], buildingId: number): string | undefined {
    const building = this.entities.get(buildingId);
    if (!building || !building.garrisonSlots || !building.occupants)
      return 'That structure cannot be garrisoned.';
    const squads = ids
      .map((id) => this.entities.get(id))
      .filter((entity): entity is Entity =>
        Boolean(
          entity &&
          entity.owner === this.playerFaction &&
          this.isGroundInfantry(entity) &&
          !entity.garrisonedIn &&
          !entity.garrisoningBuildingId &&
          !entity.embarkedIn &&
          distance(entity.position, building.position) < 100,
        ),
      );
    if (!squads.length) return 'Move infantry next to the garrison first.';
    for (const squad of squads) {
      if (building.occupants.length >= building.garrisonSlots) break;
      squad.garrisoningBuildingId = building.id;
      squad.garrisonRemaining = 1;
      squad.target = undefined;
      squad.order = 'Entering garrison';
    }
    return undefined;
  }
  public exitGarrison(buildingId: number, target: Point): string | undefined {
    const building = this.entities.get(buildingId);
    if (!building?.occupants?.length) return 'The garrison is empty.';
    if (building.exitRemaining !== undefined) return 'The garrison is already exiting.';
    building.exitTarget = this.clampPoint(target);
    building.exitRemaining = 1;
    building.order = 'Garrison exiting';
    return undefined;
  }

  private runQueues(): void {
    for (const building of this.entities.values()) {
      if (building.kind !== 'building' || building.construction || !building.queue?.[0]) continue;
      const request = building.queue[0];
      const definition = getUnit(request.unitId);
      if (!request || !definition || building.owner === 'neutral') continue;
      if (!request.reserved) {
        const player = this.players[building.owner];
        if (
          player.capacity + player.reservedCapacity + definition.capacity >
            BALANCE.capacity.commandMax ||
          player.entities + player.reservedEntities >= BALANCE.capacity.entityMax
        )
          continue;
        request.reserved = true;
        player.reservedCapacity += definition.capacity;
        player.reservedEntities += 1;
      }
      request.elapsed += TICK_SECONDS;
      if (request.elapsed < definition.requestTime) continue;
      const ingress = building.owner === 'equestria' ? { x: 4020, y: 780 } : { x: 76, y: 780 };
      const spawned = this.spawnUnit(building.owner, request.unitId, ingress, true);
      if (request.reserved) {
        this.players[building.owner].reservedCapacity -= definition.capacity;
        this.players[building.owner].reservedEntities -= 1;
      }
      if (spawned) {
        spawned.target = { ...(building.rally ?? building.position) };
        this.message(`${spawned.name} has arrived.`);
      }
      building.queue.shift();
      if (building.queue[0]) building.queue[0].active = true;
    }
  }
  private runLandingTransitions(): void {
    for (const unit of this.entities.values()) {
      if (!unit.air || unit.kind !== 'unit') continue;
      if (unit.landingTarget) {
        if (distance(unit.position, unit.landingTarget) > 24) continue;
        if (!this.isClearLandingArea(unit, unit.landingTarget)) {
          unit.landingTarget = undefined;
          unit.target = undefined;
          unit.order = 'Landing aborted: area obstructed';
          continue;
        }
        unit.position = { ...unit.landingTarget };
        unit.target = undefined;
        unit.landingTarget = undefined;
        unit.landingRemaining = 1;
        unit.order = 'Landing';
      }
      if (unit.landingRemaining === undefined) continue;
      unit.landingRemaining = Math.max(0, unit.landingRemaining - TICK_SECONDS);
      if (unit.landingRemaining > 0) continue;
      unit.landingRemaining = undefined;
      unit.landed = !unit.landed;
      unit.order = unit.landed ? 'Landed' : 'Airborne';
    }
  }
  private runTransportTransitions(): void {
    for (const passenger of this.entities.values()) {
      if (passenger.boardingTransportId === undefined || passenger.boardingRemaining === undefined)
        continue;
      passenger.boardingRemaining = Math.max(0, passenger.boardingRemaining - TICK_SECONDS);
      if (passenger.boardingRemaining > 0) continue;
      const transport = this.entities.get(passenger.boardingTransportId);
      passenger.boardingTransportId = undefined;
      passenger.boardingRemaining = undefined;
      if (
        !transport ||
        !transport.passengers ||
        !transport.transportSlots ||
        transport.passengers.length >= transport.transportSlots ||
        distance(passenger.position, transport.position) >= 90
      ) {
        passenger.order = 'Boarding interrupted';
        continue;
      }
      passenger.embarkedIn = transport.id;
      passenger.position = { ...transport.position };
      passenger.order = 'Embarked';
      transport.passengers.push(passenger.id);
    }
    for (const transport of this.entities.values()) {
      if (transport.disembarkRemaining === undefined || !transport.disembarkTarget) continue;
      transport.disembarkRemaining = Math.max(0, transport.disembarkRemaining - TICK_SECONDS);
      if (transport.disembarkRemaining > 0) continue;
      for (const id of transport.passengers ?? []) {
        const passenger = this.entities.get(id);
        if (!passenger) continue;
        passenger.embarkedIn = undefined;
        passenger.position = {
          x: transport.disembarkTarget.x + this.rng.range(-38, 38),
          y: transport.disembarkTarget.y + this.rng.range(-38, 38),
        };
        passenger.target = undefined;
        passenger.order = 'Disembarked';
      }
      transport.passengers = [];
      transport.disembarkTarget = undefined;
      transport.disembarkRemaining = undefined;
      transport.order = 'Idle';
    }
  }
  private runGarrisonTransitions(): void {
    for (const squad of this.entities.values()) {
      if (squad.garrisoningBuildingId === undefined || squad.garrisonRemaining === undefined)
        continue;
      squad.garrisonRemaining = Math.max(0, squad.garrisonRemaining - TICK_SECONDS);
      if (squad.garrisonRemaining > 0) continue;
      const building = this.entities.get(squad.garrisoningBuildingId);
      squad.garrisoningBuildingId = undefined;
      squad.garrisonRemaining = undefined;
      if (
        !building ||
        !building.occupants ||
        !building.garrisonSlots ||
        building.occupants.length >= building.garrisonSlots ||
        distance(squad.position, building.position) >= 100
      ) {
        squad.order = 'Garrison entry interrupted';
        continue;
      }
      squad.garrisonedIn = building.id;
      squad.position = { ...building.position };
      squad.order = 'Garrisoned';
      building.occupants.push(squad.id);
    }
    for (const building of this.entities.values()) {
      if (building.exitRemaining === undefined || !building.exitTarget) continue;
      building.exitRemaining = Math.max(0, building.exitRemaining - TICK_SECONDS);
      if (building.exitRemaining > 0) continue;
      for (const id of building.occupants ?? []) {
        const squad = this.entities.get(id);
        if (!squad) continue;
        squad.garrisonedIn = undefined;
        squad.position = {
          x: building.exitTarget.x + this.rng.range(-40, 40),
          y: building.exitTarget.y + this.rng.range(-40, 40),
        };
        squad.order = 'Exited garrison';
      }
      building.occupants = [];
      building.exitTarget = undefined;
      building.exitRemaining = undefined;
      building.order = 'Garrison building';
    }
  }
  private runConstruction(): void {
    for (const building of this.entities.values())
      if (building.construction) {
        building.construction = Math.max(0, building.construction - TICK_SECONDS);
        if (building.construction === 0) {
          building.order = 'Operational';
          this.message(`${building.name} is operational.`);
        }
      }
  }
  /**
   * Guided-weapon lock mechanic: accumulates lock progress when target is visible
   * and not inside smoke/EW. Lock is cancelled if smoke/EW intervenes or target dies.
   */
  private runGuidedLocks(): void {
    for (const entity of this.entities.values()) {
      if (entity.lockTarget === undefined || entity.lockProgress === undefined) continue;
      const target = this.entities.get(entity.lockTarget);
      if (!target || target.health <= 0) {
        entity.lockTarget = undefined;
        entity.lockProgress = undefined;
        entity.order = 'Lock lost: target unavailable';
        continue;
      }
      if (this.lineBlockedBySmoke(entity.position, target.position)) {
        entity.lockTarget = undefined;
        entity.lockProgress = undefined;
        entity.order = 'Lock cancelled: smoke interference';
        continue;
      }
      // EW in area doubles the lock time requirement.
      const ewPenalty = this.isInsideEW(entity) ? 2 : 1;
      entity.lockProgress += TICK_SECONDS / ewPenalty;
    }
  }
  /** Checks if an entity is inside any hostile EW area. */
  private isInsideEW(entity: Entity): boolean {
    return [...this.effects.values()].some(
      (effect) =>
        effect.kind === 'smoke' &&
        effect.label.includes('Electronic') &&
        effect.owner !== entity.owner &&
        distance(effect.position, entity.position) <= effect.radius,
    );
  }
  /** Artillery/ATGM setup transition. */
  private runSetup(): void {
    for (const entity of this.entities.values()) {
      if (entity.setupRemaining === undefined) continue;
      entity.setupRemaining = Math.max(0, entity.setupRemaining - TICK_SECONDS);
      if (entity.setupRemaining > 0) continue;
      entity.setupRemaining = undefined;
      entity.deployed = true;
      entity.order = `${entity.name} set up`;
    }
  }
  private runMovement(): void {
    for (const entity of this.entities.values()) {
      entity.previous = { ...entity.position };
      if (entity.embarkedIn) {
        const transport = this.entities.get(entity.embarkedIn);
        if (transport) entity.position = { ...transport.position };
        continue;
      }
      if (entity.garrisonedIn) {
        const building = this.entities.get(entity.garrisonedIn);
        if (building) entity.position = { ...building.position };
        continue;
      }
      if (entity.deployed) continue;
      if (!entity.target || entity.speed <= 0) continue;
      const target = entity.waypoints?.[0] ?? entity.target;
      const gap = distance(entity.position, target);
      const suppressionLevel =
        entity.suppression !== undefined &&
        entity.suppression >= BALANCE.suppression.pinnedThreshold
          ? 'pinned'
          : entity.suppression !== undefined &&
              entity.suppression >= BALANCE.suppression.suppressedThreshold
            ? 'suppressed'
            : 'normal';
      const suppressionSpeed =
        suppressionLevel === 'pinned'
          ? BALANCE.suppression.pinnedSpeedMultiplier
          : suppressionLevel === 'suppressed'
            ? BALANCE.suppression.suppressedSpeedMultiplier
            : 1;
      const speed =
        (entity.retreating ? entity.speed * 1.5 : entity.speed) *
        (entity.critical === 'engine' ? 0.45 : 1) *
        (entity.evasiveUntil && entity.evasiveUntil > this.time ? 1.6 : 1) *
        (entity.relocationUntil && entity.relocationUntil > this.time ? 1.45 : 1) *
        (entity.commandedUntil && entity.commandedUntil > this.time ? 1.12 : 1) *
        suppressionSpeed *
        (this.inHostileHeadwind(entity) ? 0.6 : 1);
      if (speed === 0 && !entity.retreating) {
        entity.order = 'Pinned';
        if (entity.members) entity.formation = 'compact';
        continue;
      }
      if (gap <= speed * TICK_SECONDS) {
        entity.position = { ...target };
        if (entity.waypoints?.length) entity.waypoints.shift();
        if (!entity.waypoints?.length) entity.target = undefined;
        if (entity.arriving) entity.arriving = false;
        if (!entity.retreating) {
          entity.order = entity.repairTarget
            ? `Repairing ${this.entities.get(entity.repairTarget)?.name ?? 'vehicle'}`
            : 'Idle';
          if (entity.formation && entity.formation !== 'garrison')
            entity.formation = entity.garrisonedIn
              ? 'garrison'
              : entity.cover && entity.cover !== 'none'
                ? 'compact'
                : 'line';
        }
        if (entity.retreating) {
          entity.retreating = false;
          entity.formation = 'line';
          entity.order = 'At headquarters';
        }
        continue;
      }
      const direction = angleTo(entity.position, target);
      const nextPosition = {
        x: entity.position.x + Math.cos(direction) * speed * TICK_SECONDS,
        y: entity.position.y + Math.sin(direction) * speed * TICK_SECONDS,
      };
      const wire = [...this.entities.values()].find(
        (obstacle) => obstacle.wire && distance(obstacle.position, nextPosition) < 34,
      );
      if (wire && this.isGroundInfantry(entity)) {
        entity.target = undefined;
        entity.waypoints = undefined;
        entity.order = 'Blocked by barbed wire';
        continue;
      }
      if (wire && entity.category === 'vehicle') {
        this.entities.delete(wire.id);
        this.message(`${entity.name} crushed barbed wire.`);
      }
      entity.facing = direction;
      entity.position = nextPosition;
      if (entity.formation && entity.formation !== 'retreat' && entity.formation !== 'garrison')
        entity.formation = 'loose';
    }
  }
  private runMines(): void {
    for (const mine of [...this.entities.values()].filter((entity) => entity.mine)) {
      const victim = [...this.entities.values()].find(
        (entity) =>
          entity.kind === 'unit' &&
          entity.owner !== mine.owner &&
          entity.owner !== 'neutral' &&
          entity.category === 'vehicle' &&
          !entity.air &&
          distance(entity.position, mine.position) < 38,
      );
      if (!victim) continue;
      victim.health -= 105;
      victim.critical = 'engine';
      this.entities.delete(mine.id);
      this.message(`${victim.name} triggered an anti-vehicle mine.`);
      if (victim.health <= 0) this.destroy(victim);
    }
  }
  private runCapture(): void {
    let recalc = false;
    for (const sector of sectors) {
      if (sector.type === 'base') continue;
      const state = this.sectorState.get(sector.id);
      if (!state) continue;
      const nearby = [...this.entities.values()].filter(
        (entity) =>
          entity.kind === 'unit' &&
          this.isGroundInfantry(entity) &&
          !entity.embarkedIn &&
          !entity.boardingTransportId &&
          !entity.garrisoningBuildingId &&
          !entity.landingTarget &&
          entity.landingRemaining === undefined &&
          !entity.retreating &&
          (entity.suppression ?? 0) < BALANCE.suppression.suppressedThreshold &&
          pointInCircle(entity.position, sector.point, BALANCE.capture.radius),
      );
      const captureContribution = (entity: Entity): number => {
        const reconnaissance = /observer|recon/.test(entity.definitionId);
        const base = reconnaissance ? VETERANCY.reconnaissanceCaptureMultiplier : 1;
        return base * (1 + entity.veterancy * VETERANCY.capturePerLevel);
      };
      const eq = nearby
        .filter((entry) => entry.owner === 'equestria')
        .reduce((total, entry) => total + captureContribution(entry), 0);
      const idf = nearby
        .filter((entry) => entry.owner === 'interdef')
        .reduce((total, entry) => total + captureContribution(entry), 0);
      state.contested = eq > 0 && idf > 0;
      if (state.contested || (eq === 0 && idf === 0)) continue;
      const capturing: Faction = eq > 0 ? 'equestria' : 'interdef';
      const rawStrength = eq || idf;
      const strength = Math.min(
        BALANCE.capture.maxStackedStrength,
        rawStrength / (1 + Math.max(0, rawStrength - 1) * BALANCE.capture.stackDiminishingFactor),
      );
      if (state.owner === capturing) {
        state.progress = clamp(
          state.progress + TICK_SECONDS * BALANCE.capture.progressPerSecond,
          0,
          100,
        );
        continue;
      }
      if (state.owner === 'neutral') {
        state.progress = clamp(
          state.progress + (TICK_SECONDS / BALANCE.capture.neutralSeconds) * 100 * strength,
          0,
          100,
        );
        if (state.progress < 100) continue;
        state.owner = capturing;
        recalc = true;
        this.message(`${sector.name} is now ${capturing} controlled.`);
        continue;
      }
      state.progress -= TICK_SECONDS * BALANCE.capture.neutralizePerSecond * strength;
      if (state.progress <= 0) {
        state.owner = 'neutral';
        state.progress = 0;
        recalc = true;
        this.message(`${sector.name} is now neutral.`);
      }
    }
    if (recalc) this.recalculateConnections();
  }
  private runEffects(): void {
    for (const effect of this.effects.values()) {
      effect.remaining -= TICK_SECONDS;
      if (effect.kind === 'damage' && !effect.armed && effect.remaining <= 0) {
        effect.armed = true;
        for (const entity of this.entities.values()) {
          if (
            entity.kind === 'wreck' ||
            entity.owner === 'neutral' ||
            distance(entity.position, effect.position) > effect.radius
          )
            continue;
          const falloff = 1 - distance(entity.position, effect.position) / effect.radius;
          const dealt =
            effect.damage * Math.max(0.25, falloff) * (entity.cover === 'heavy' ? 0.85 : 1);
          entity.health -= dealt;
          if (entity.suppression !== undefined)
            entity.suppression = clamp(entity.suppression + 30 * falloff, 0, 100);
          if (entity.health <= 0) this.destroy(entity);
        }
        this.message(`${effect.label} impacts.`);
      }
      if (effect.remaining <= -0.25 || (effect.kind === 'damage' && effect.armed))
        this.effects.delete(effect.id);
    }
  }
  private runCombat(): void {
    const current = [...this.entities.values()];
    for (const entity of current) {
      entity.reload = Math.max(0, entity.reload - TICK_SECONDS);
      for (const key of Object.keys(entity.cooldowns ?? {})) {
        const cooldown = entity.cooldowns?.[key] ?? 0;
        if (cooldown > 0 && entity.cooldowns)
          entity.cooldowns[key] = Math.max(0, cooldown - TICK_SECONDS);
      }
      if (
        entity.damage <= 0 ||
        entity.reload > 0 ||
        entity.critical === 'weapon' ||
        (entity.suppression ?? 0) >= BALANCE.suppression.pinnedThreshold ||
        entity.holdFire ||
        entity.kind === 'wreck' ||
        entity.embarkedIn ||
        entity.boardingTransportId ||
        entity.garrisoningBuildingId ||
        (entity.evasiveUntil !== undefined && entity.evasiveUntil > this.time) ||
        (entity.air && entity.landed && !this.isGroundInfantry(entity))
      )
        continue;
      let target = entity.targetEntity ? this.entities.get(entity.targetEntity) : undefined;
      if (
        !target ||
        target.owner === entity.owner ||
        target.health <= 0 ||
        (this.isAirborne(target) && !entity.antiAir && !this.isAirborne(entity))
      ) {
        const acquisitionRange = entity.range + (entity.deployed ? 80 : 0);
        target = current.find(
          (candidate) =>
            candidate.owner !== entity.owner &&
            candidate.owner !== 'neutral' &&
            candidate.health > 0 &&
            distance(entity.position, candidate.position) <= acquisitionRange &&
            (!this.isAirborne(candidate) || entity.antiAir || this.isAirborne(entity)),
        );
      }
      const effectiveRange = entity.range + (entity.deployed ? 80 : 0);
      if (!target || distance(entity.position, target.position) > effectiveRange) continue;
      if (this.lineBlockedBySmoke(entity.position, target.position)) continue;
      if (entity.category === 'vehicle') {
        const desiredTurretFacing = angleTo(entity.position, target.position);
        const turretFacing = entity.turretFacing ?? entity.facing;
        const turnDelta = normalizeAngle(desiredTurretFacing - turretFacing);
        entity.turretFacing = normalizeAngle(
          turretFacing + clamp(turnDelta, -2.8 * TICK_SECONDS, 2.8 * TICK_SECONDS),
        );
        if (Math.abs(turnDelta) > 0.16) continue;
      }
      const facingDelta = Math.abs(
        normalizeAngle(target.facing - angleTo(target.position, entity.position)),
      );
      const armor =
        target.category === 'vehicle'
          ? target.armor * (facingDelta < 0.95 ? 1 : facingDelta > 2.35 ? 0.55 : 0.75)
          : 0;
      const incomingDirection = angleTo(target.position, entity.position);
      const protectedByCover =
        target.garrisonedIn !== undefined ||
        (target.coverFacing !== undefined &&
          Math.abs(normalizeAngle(incomingDirection - target.coverFacing)) < Math.PI / 2);
      const coverAccuracy =
        protectedByCover && target.cover === 'heavy'
          ? 0.5
          : protectedByCover && target.cover === 'light'
            ? 0.75
            : 1;
      const hit =
        this.rng.next() <
        clamp(
          0.72 *
            (1 + entity.veterancy * VETERANCY.accuracyPerLevel) *
            ((entity.suppression ?? 0) >= BALANCE.suppression.suppressedThreshold
              ? BALANCE.suppression.suppressedAccuracyMultiplier
              : 1) *
            coverAccuracy,
          0.05,
          0.95,
        );
      const countermeasures =
        this.isAirborne(target) &&
        target.countermeasuresUntil !== undefined &&
        target.countermeasuresUntil > this.time;
      if (!hit || (countermeasures && this.rng.next() < 0.55)) {
        entity.reload = 0.75;
        continue;
      }
      const ammunitionMultiplier =
        entity.ammunition === 'he' && target.category === 'infantry' ? 1.35 : 1;
      const squadFirepower =
        entity.members !== undefined && entity.maxMembers !== undefined
          ? entity.members / entity.maxMembers
          : 1;
      const suppressiveFire = /m2hb|hmg|machine_gun/.test(entity.definitionId);
      const directFireMultiplier = suppressiveFire && target.suppression !== undefined ? 0.35 : 1;
      const penetration =
        armor === 0 ||
        this.rng.next() < clamp((0.5 * entity.damage) / Math.max(1, armor), 0.05, 0.95);
      const ambushMultiplier = entity.ambushReady ? 1.65 : 1;
      const veteranProtection =
        target.category === 'infantry'
          ? 1 - target.veterancy * VETERANCY.infantryDamageReductionPerLevel
          : 1;
      const dealt = penetration
        ? entity.damage *
          ammunitionMultiplier *
          squadFirepower *
          ambushMultiplier *
          directFireMultiplier *
          veteranProtection
        : Math.max(1, entity.damage * 0.08 * squadFirepower * veteranProtection);
      entity.ambushReady = undefined;
      const absorbed = Math.min(target.shield ?? 0, dealt);
      target.shield = Math.max(0, (target.shield ?? 0) - absorbed);
      target.health -= dealt - absorbed;
      if (target.memberHealth) this.applySquadDamage(target, dealt - absorbed);
      if (target.members && target.maxMembers) {
        const expectedMembers = Math.max(
          0,
          Math.ceil((target.health / target.maxHealth) * target.maxMembers),
        );
        if (expectedMembers < (target.members ?? 0)) target.members = expectedMembers;
      }
      this.updateMorale(target);
      entity.experience += dealt * 0.08;
      if (entity.experience > 100 * (entity.veterancy + 1) && entity.veterancy < 3)
        entity.veterancy += 1;
      entity.reload =
        (entity.category === 'vehicle' ? 1.35 : 0.75) /
        (entity.commandedUntil && entity.commandedUntil > this.time ? 1.15 : 1);
      if (target.suppression !== undefined && (entity.damage > 15 || suppressiveFire))
        target.suppression = clamp(
          target.suppression + (suppressiveFire ? entity.damage * 4.5 : entity.damage * 0.18),
          0,
          100,
        );
      if (
        target.health < target.maxHealth * 0.5 &&
        target.category === 'vehicle' &&
        this.rng.next() < 0.04
      )
        target.critical = this.rng.next() < 0.5 ? 'engine' : 'weapon';
      if (target.health <= 0) this.destroy(target);
    }
  }
  private runHealing(): void {
    for (const entity of this.entities.values()) {
      const recovery =
        (entity.cover === 'heavy' ? 8 : entity.cover === 'light' ? 6 : 4) +
        (entity.category === 'infantry'
          ? entity.veterancy * VETERANCY.suppressionRecoveryPerLevel
          : 0);
      if (entity.suppression !== undefined)
        entity.suppression = Math.max(0, entity.suppression - TICK_SECONDS * recovery);
      if (entity.retreating || entity.kind !== 'unit') continue;
      const medic = [...this.entities.values()].some(
        (other) =>
          other.owner === entity.owner &&
          (other.definitionId === hqId(entity.owner as Faction) ||
            (other.deployed &&
              (other.definitionId.includes('savior') || other.definitionId.includes('m1133')))) &&
          distance(other.position, entity.position) < 130,
      );
      if (!medic) continue;
      entity.health = Math.min(entity.maxHealth, entity.health + TICK_SECONDS * 8);
      if (entity.suppression !== undefined)
        entity.suppression = Math.max(0, entity.suppression - TICK_SECONDS * 8);
      if (
        entity.members !== undefined &&
        entity.maxMembers !== undefined &&
        entity.members < entity.maxMembers &&
        entity.owner !== 'neutral'
      ) {
        const owner = entity.owner as Faction;
        const atReinforcementPoint = [...this.entities.values()].some(
          (other) =>
            other.owner === owner &&
            distance(other.position, entity.position) < 135 &&
            (other.definitionId === hqId(owner) ||
              (other.deployed &&
                this.isConnectedFriendlyPosition(owner, other.position) &&
                (other.definitionId.includes('savior') ||
                  other.definitionId.includes('m1133') ||
                  other.definitionId.includes('wc30') ||
                  other.definitionId.includes('m113a3')))),
        );
        if (atReinforcementPoint) {
          const contested = [...this.entities.values()].some(
            (other) =>
              other.owner !== owner &&
              other.owner !== 'neutral' &&
              other.kind === 'unit' &&
              !other.embarkedIn &&
              distance(other.position, entity.position) < 170,
          );
          if (contested) continue;
          entity.reinforceProgress = (entity.reinforceProgress ?? 0) + TICK_SECONDS;
          if (entity.reinforceProgress >= 3 && this.players[owner].supply >= 30) {
            this.players[owner].supply -= 30;
            entity.members += 1;
            entity.reinforceProgress = 0;
            entity.health = Math.max(
              entity.health,
              (entity.members / entity.maxMembers) * entity.maxHealth * 0.65,
            );
            this.message(`${entity.name} reinforced.`);
          }
        }
      }
    }
    for (const vehicle of this.entities.values()) {
      if (
        vehicle.kind !== 'unit' ||
        vehicle.category !== 'vehicle' ||
        (vehicle.health >= vehicle.maxHealth && !vehicle.critical) ||
        vehicle.owner === 'neutral'
      )
        continue;
      const repairer = [...this.entities.values()].find(
        (other) =>
          other.id !== vehicle.id &&
          other.owner === vehicle.owner &&
          other.kind === 'unit' &&
          (other.definitionId.includes('engineer') || other.definitionId.includes('m88a2')) &&
          !other.embarkedIn &&
          distance(other.position, vehicle.position) < 100,
      );
      if (!repairer) continue;
      vehicle.health = Math.min(vehicle.maxHealth, vehicle.health + TICK_SECONDS * 18);
      if (vehicle.critical && vehicle.health >= vehicle.maxHealth * 0.6) {
        vehicle.critical = undefined;
        this.message(`${repairer.name} repaired ${vehicle.name}.`);
      }
      if (vehicle.health >= vehicle.maxHealth && !vehicle.critical) {
        for (const other of this.entities.values())
          if (other.repairTarget === vehicle.id) {
            other.repairTarget = undefined;
            other.target = undefined;
            other.order = 'Idle';
          }
      }
    }
  }
  private runVisibility(): void {
    const observers = [...this.entities.values()].filter(
      (entity) =>
        entity.owner === this.playerFaction && entity.kind !== 'wreck' && !entity.embarkedIn,
    );
    const coverPoints: Point[] = [
      { x: 1410, y: 310 },
      { x: 2020, y: 760 },
      { x: 1880, y: 1350 },
      { x: 1210, y: 750 },
      { x: 2700, y: 970 },
      ...[...this.entities.values()]
        .filter((entity) => entity.fortification && entity.health > 0)
        .map((entity) => entity.position),
    ];
    for (const entity of this.entities.values()) {
      const nearestCover = coverPoints
        .map((point) => ({ point, distance: distance(point, entity.position) }))
        .sort((a, b) => a.distance - b.distance)[0];
      entity.cover = entity.garrisonedIn
        ? 'heavy'
        : entity.category !== 'infantry' && !(entity.air && entity.landed)
          ? 'none'
          : (nearestCover?.distance ?? Infinity) < 42
            ? 'heavy'
            : (nearestCover?.distance ?? Infinity) < 95
              ? 'light'
              : 'none';
      entity.coverFacing =
        entity.cover !== 'none' && !entity.garrisonedIn && nearestCover
          ? angleTo(entity.position, nearestCover.point)
          : undefined;
      if (entity.owner === this.playerFaction || entity.owner === 'neutral') {
        entity.visible = true;
        continue;
      }
      entity.visible = observers.some(
        (observer) =>
          distance(observer.position, entity.position) <= this.sightRange(observer) &&
          (!entity.concealed ||
            (this.isDetector(observer) && distance(observer.position, entity.position) <= 170)) &&
          !this.lineBlockedBySmoke(observer.position, entity.position),
      );
    }
  }
  private runEconomy(): void {
    if (Math.floor(this.time * 10) % 10 !== 0) return;
    for (const faction of ['equestria', 'interdef'] as const) {
      const player = this.players[faction];
      let connected = 0;
      let cutoffs = 0;
      for (const sector of sectors.filter((entry) => entry.type === 'supply')) {
        const state = this.sectorState.get(sector.id);
        if (state?.owner === faction) {
          if (state.connected[faction]) connected++;
          else cutoffs++;
        }
      }
      player.depots = connected;
      player.cutoffs = cutoffs;
      player.supply +=
        (BALANCE.economy.baseIncome + connected * BALANCE.economy.depotIncome) *
        0.1 *
        (faction !== this.playerFaction ? aiDifficulty[this.difficulty].incomeMultiplier : 1);
    }
  }
  private runVictory(): void {
    if (this.mode === 'annihilation') {
      for (const faction of ['equestria', 'interdef'] as const)
        if (!this.findHeadquarters(faction)) this.finish(enemy(faction));
      return;
    }
    for (const faction of ['equestria', 'interdef'] as const)
      if (!this.findHeadquarters(faction) && !this.canRecoverBase(faction))
        this.finish(enemy(faction));
    if (this._winner) return;
    this.ticketAccumulator += TICK_SECONDS;
    if (this.ticketAccumulator < BALANCE.economy.ticketDrainInterval) return;
    this.ticketAccumulator = 0;
    const held = { equestria: 0, interdef: 0 };
    for (const sector of sectors.filter((entry) => entry.type === 'victory')) {
      const state = this.sectorState.get(sector.id);
      if (state && !state.contested && state.owner !== 'neutral') held[state.owner]++;
    }
    const delta = held.equestria - held.interdef;
    if (delta > 0) this.players.interdef.tickets -= delta;
    if (delta < 0) this.players.equestria.tickets += delta;
    if (this.players.equestria.tickets <= 0) this.finish('interdef');
    if (this.players.interdef.tickets <= 0) this.finish('equestria');
  }
  private finish(winner: Faction): void {
    if (!this._winner) {
      this._winner = winner;
      this.message(`${winner} wins the match.`);
    }
  }
  private runAi(): void {
    const faction = enemy(this.playerFaction);
    if (this._winner) return;
    const aiEntities = [...this.entities.values()].filter(
      (entry) => entry.owner === faction && entry.kind === 'unit',
    );
    const engineer = aiEntities.find((entry) => entry.definitionId.includes('engineer'));
    const hq = this.findHeadquarters(faction);
    if (!hq) return;
    this.runAiRepairs(aiEntities, faction);
    this.runAiPreservation(aiEntities, faction, hq);
    const chain = buildings.filter((building) => building.faction === faction);
    const difficulty = aiDifficulty[this.difficulty];
    const nextBuilding = chain.find(
      (building) =>
        !this.hasBuilding(faction, building.id) &&
        this.hasPrerequisites(faction, building.prerequisites) &&
        this.players[faction].supply >= building.cost,
    );
    if (nextBuilding && engineer && this.rng.next() <= difficulty.buildChance) {
      const existing = [...this.entities.values()].filter(
        (entry) => entry.owner === faction && entry.kind === 'building',
      ).length;
      this.players[faction].supply -= nextBuilding.cost;
      this.spawnBuilding(
        faction,
        nextBuilding.id,
        {
          x: hq.position.x + (faction === 'equestria' ? -1 : 1) * (150 + (existing % 2) * 130),
          y: hq.position.y + (existing < 4 ? (existing - 2) * 145 : 260),
        },
        nextBuilding.buildTime,
      );
      this.message(`${faction} AI begins ${nextBuilding.name}.`);
      return;
    }
    const knownEnemy = this.allEntities().filter(
      (entry) =>
        entry.owner === this.playerFaction &&
        !entry.embarkedIn &&
        this.isVisibleToFaction(faction, entry),
    );
    const needsAirDefense = knownEnemy.some((entry) => this.isAirborne(entry));
    const needsAntiArmor = knownEnemy.some(
      (entry) => entry.category === 'vehicle' && entry.armor > 60,
    );
    const support = abilities.find(
      (ability) =>
        ability.faction === faction &&
        this.hasBuilding(faction, ability.source) &&
        this.players[faction].supply >= ability.supplyCost &&
        (hq.cooldowns?.[ability.id] ?? 0) <= 0,
    );
    const supportTarget = knownEnemy.sort(
      (a, b) => distance(a.position, hq.position) - distance(b.position, hq.position),
    )[0];
    if (support && supportTarget && this.rng.next() < 0.25) {
      const kind: AreaEffect['kind'] =
        support.kind === 'damage' || support.kind === 'smoke' || support.kind === 'recon'
          ? support.kind
          : 'recon';
      this.players[faction].supply -= support.supplyCost;
      this.addEffect(
        faction,
        kind,
        supportTarget.position,
        support.radius,
        support.delay ?? 8,
        support.damage ?? 0,
        support.name,
      );
      this.setCooldown(hq, support.id, support.cooldown);
      this.message(`${faction} AI calls ${support.name}.`);
    }
    this.runAiUnitAbilities(aiEntities, faction, knownEnemy, difficulty.tacticalAbilityChance);
    for (const building of this.entities.values()) {
      if (
        building.owner !== faction ||
        building.kind !== 'building' ||
        building.construction ||
        !building.queue ||
        building.queue.length >= 2
      )
        continue;
      const options = buildings.find((entry) => entry.id === building.definitionId)?.unlocks ?? [];
      const priority =
        options.find(
          (id) => needsAirDefense && (id.includes('stinger') || id.includes('shorad')),
        ) ??
        options.find(
          (id) =>
            needsAntiArmor &&
            (id.includes('at') ||
              id.includes('fgm') ||
              id.includes('m1134') ||
              id.includes('peregrine') ||
              id.includes('abrams') ||
              id.includes('sledgehammer')),
        ) ??
        options[Math.floor(this.rng.next() * options.length)];
      const definition = priority ? getUnit(priority) : undefined;
      if (
        !priority ||
        !definition ||
        this.players[faction].supply < definition.cost ||
        this.players[faction].capacity + definition.capacity > 120 ||
        this.players[faction].entities >= 40
      )
        continue;
      this.players[faction].supply -= definition.cost;
      building.queue.push({
        id: this.nextRequest++,
        unitId: priority,
        elapsed: 0,
        active: building.queue.length === 0,
        reserved: false,
      });
    }
    const captureTarget = sectors
      .filter((sector) => sector.type !== 'base')
      .sort((a, b) => {
        const aState = this.sectorState.get(a.id);
        const bState = this.sectorState.get(b.id);
        return (
          (aState?.owner === faction ? 10000 : 0) +
          distance(hq.position, a.point) -
          ((bState?.owner === faction ? 10000 : 0) + distance(hq.position, b.point))
        );
      })[0];
    const threatenedSector = sectors
      .filter((sector) => this.sectorState.get(sector.id)?.owner === faction)
      .map((sector) => ({
        sector,
        threats: knownEnemy.filter((unit) => distance(unit.position, sector.point) < 210).length,
      }))
      .sort((a, b) => b.threats - a.threats)[0];
    const baseThreat = knownEnemy.some((unit) => distance(unit.position, hq.position) < 560);
    const idle = aiEntities.filter(
      (unit) => !unit.target && !unit.retreating && !unit.repairTarget && !unit.deployed,
    );
    const defensePoint = baseThreat ? hq.position : threatenedSector?.sector.point;
    const defenseCount = defensePoint ? Math.max(1, Math.floor(difficulty.taskForceSize / 3)) : 0;
    for (const entry of idle.slice(0, defenseCount)) {
      entry.target = { ...defensePoint! };
      entry.order = baseThreat ? 'AI defend base' : 'AI defend sector';
    }
    const remaining = idle.slice(defenseCount);
    const captureCount = Math.ceil(difficulty.taskForceSize * 0.6);
    if (captureTarget)
      for (const entry of remaining.slice(0, captureCount)) {
        entry.target = { ...captureTarget.point };
        entry.order = 'AI capture';
      }
    if (captureTarget)
      for (const [index, entry] of remaining
        .slice(captureCount, difficulty.taskForceSize)
        .entries()) {
        entry.target = {
          x: captureTarget.point.x,
          y: clamp(captureTarget.point.y + (index % 2 === 0 ? 150 : -150), 70, WORLD.height - 70),
        };
        entry.order = 'AI flank';
      }
    const artillery = aiEntities.find(
      (unit) => unit.definitionId.includes('paladin') || unit.definitionId.includes('sh32'),
    );
    const artilleryTarget = knownEnemy
      .filter((unit) => artillery && distance(unit.position, artillery.position) > 180)
      .sort((a, b) => this.aiTargetValue(b, knownEnemy) - this.aiTargetValue(a, knownEnemy))[0];
    if (
      artillery &&
      artilleryTarget &&
      artillery.reload <= 0 &&
      this.aiTargetValue(artilleryTarget, knownEnemy) >= 2 &&
      this.rng.next() < 0.65
    ) {
      this.addEffect(
        faction,
        'damage',
        artilleryTarget.position,
        95,
        1.5,
        115,
        'AI artillery barrage',
      );
      artillery.reload = 14;
    }
  }
  private runAiUnitAbilities(
    aiEntities: Entity[],
    faction: Faction,
    knownEnemy: Entity[],
    tacticalAbilityChance: number,
  ): void {
    for (const unit of aiEntities) {
      if (
        unit.embarkedIn ||
        unit.retreating ||
        unit.boardingTransportId ||
        unit.garrisoningBuildingId
      )
        continue;
      const definition = getUnit(unit.definitionId);
      if (!definition?.abilities.length) continue;
      const target = knownEnemy
        .filter(
          (entry) => distance(entry.position, unit.position) <= Math.max(420, unit.range + 140),
        )
        .sort((a, b) => this.aiTargetValue(b, knownEnemy) - this.aiTargetValue(a, knownEnemy))[0];
      if (target && this.rng.next() > tacticalAbilityChance) continue;
      const has = (pattern: RegExp): string | undefined =>
        definition.abilities.find((name) => pattern.test(this.slug(name)));
      const ready = (ability: string | undefined): ability is string =>
        Boolean(ability && (unit.cooldowns?.[this.slug(ability)] ?? 0) <= 0);
      const smoke = has(/smoke|cloud|distortion/);
      if (
        ready(smoke) &&
        target &&
        unit.health < unit.maxHealth * 0.65 &&
        distance(unit.position, target.position) < 260
      ) {
        this.addEffect(faction, 'smoke', unit.position, 120, 12, 0, `AI ${smoke}`);
        this.setCooldown(unit, this.slug(smoke), 20);
        unit.order = `AI ${smoke}`;
        continue;
      }
      const evasive = has(/evasive_break|countermeasure/);
      if (ready(evasive) && this.isAirborne(unit) && unit.health < unit.maxHealth * 0.5 && target) {
        unit.evasiveUntil = this.time + 6;
        this.setCooldown(unit, this.slug(evasive), 28);
        unit.order = 'AI evasive break';
        continue;
      }
      const deploy = has(/deploy_stabilizers|observation_position|set_up/);
      if (
        ready(deploy) &&
        !unit.deployed &&
        target &&
        distance(unit.position, target.position) > unit.range * 0.65 &&
        distance(unit.position, target.position) <= unit.range + 80
      ) {
        unit.deployed = true;
        unit.target = undefined;
        unit.waypoints = undefined;
        this.setCooldown(unit, this.slug(deploy), 4);
        unit.order = `AI ${deploy}`;
        continue;
      }
      const recon = has(/designate|reconnaissance_pulse|active_scan|observation_drone/);
      if (ready(recon) && target && this.aiTargetValue(target, knownEnemy) >= 2) {
        this.addEffect(faction, 'recon', target.position, 150, 10, 0, `AI ${recon}`);
        this.setCooldown(unit, this.slug(recon), 20);
        unit.order = `AI ${recon}`;
        continue;
      }
      const strike = has(/rocket|volley|focused|top_attack|grenade|assault|canister/);
      if (!ready(strike) || !target || this.aiTargetValue(target, knownEnemy) < 2) continue;
      const isPrecision = /focused|top_attack/.test(this.slug(strike));
      this.addEffect(
        faction,
        'damage',
        target.position,
        isPrecision ? 55 : 82,
        1.2,
        Math.max(35, unit.damage * (isPrecision ? 2.1 : 1.45)),
        `AI ${strike}`,
      );
      this.setCooldown(unit, this.slug(strike), isPrecision ? 22 : 16);
      unit.order = `AI ${strike}`;
    }
  }
  private aiTargetValue(target: Entity, knownEnemy: Entity[]): number {
    const nearby = knownEnemy.filter(
      (entry) => distance(entry.position, target.position) < 110,
    ).length;
    const armorValue = target.category === 'vehicle' ? (target.armor >= 60 ? 2 : 1) : 0;
    const artilleryValue = /paladin|sh32/.test(target.definitionId) ? 2 : 0;
    return nearby + armorValue + artilleryValue;
  }
  private runAiRepairs(aiEntities: Entity[], faction: Faction): void {
    const damaged = aiEntities
      .filter(
        (unit) =>
          unit.category === 'vehicle' &&
          !unit.embarkedIn &&
          (unit.health < unit.maxHealth * 0.72 || unit.critical),
      )
      .sort((a, b) => a.health / a.maxHealth - b.health / b.maxHealth);
    const repairers = aiEntities.filter(
      (unit) =>
        !unit.embarkedIn &&
        !unit.retreating &&
        (unit.definitionId.includes('engineer') || unit.definitionId.includes('m88a2')),
    );
    for (const repairer of repairers) {
      const assigned = repairer.repairTarget ? this.entities.get(repairer.repairTarget) : undefined;
      const patient =
        assigned &&
        assigned.owner === faction &&
        assigned.category === 'vehicle' &&
        (assigned.health < assigned.maxHealth || assigned.critical)
          ? assigned
          : damaged.find(
              (vehicle) => !repairers.some((other) => other.repairTarget === vehicle.id),
            );
      if (!patient) {
        if (repairer.repairTarget) {
          repairer.repairTarget = undefined;
          repairer.target = undefined;
          repairer.order = 'Idle';
        }
        continue;
      }
      repairer.repairTarget = patient.id;
      repairer.target = { ...patient.position };
      repairer.order = `AI repair: ${patient.name}`;
    }
  }
  private runAiPreservation(aiEntities: Entity[], faction: Faction, hq: Entity): void {
    for (const unit of aiEntities) {
      const threatened = [...this.entities.values()].some(
        (enemyUnit) =>
          enemyUnit.owner === enemy(faction) &&
          enemyUnit.kind === 'unit' &&
          this.isVisibleToFaction(faction, enemyUnit) &&
          distance(unit.position, enemyUnit.position) < 310,
      );
      if (
        unit.category === 'infantry' &&
        !unit.retreating &&
        threatened &&
        (unit.health < unit.maxHealth * 0.45 || (unit.suppression ?? 0) >= 95)
      ) {
        unit.target = { ...hq.position };
        unit.targetEntity = undefined;
        unit.waypoints = undefined;
        unit.retreating = true;
        unit.order = 'AI retreat';
        continue;
      }
      if (
        unit.category === 'vehicle' &&
        !unit.repairTarget &&
        threatened &&
        (unit.health < unit.maxHealth * 0.3 || unit.critical)
      ) {
        unit.target = { ...hq.position };
        unit.targetEntity = undefined;
        unit.waypoints = undefined;
        unit.order = 'AI withdraw for repair';
      }
    }
  }
  private isVisibleToFaction(faction: Faction, target: Entity): boolean {
    if (target.owner === faction || target.owner === 'neutral') return true;
    return [...this.entities.values()].some(
      (observer) =>
        observer.owner === faction &&
        observer.kind !== 'wreck' &&
        !observer.embarkedIn &&
        (!target.concealed ||
          (this.isDetector(observer) && distance(observer.position, target.position) <= 170)) &&
        distance(observer.position, target.position) <= this.sightRange(observer) &&
        !this.lineBlockedBySmoke(observer.position, target.position),
    );
  }
  private isDetector(entity: Entity): boolean {
    return /engineer|observer|recon|fennek|rv32|shadow/.test(entity.definitionId);
  }
  private sightRange(entity: Entity): number {
    return (entity.category === 'infantry' ? 310 : 410) + (entity.sightBonus ?? 0);
  }
  private isAirborne(entity: Entity): boolean {
    return entity.air && !entity.landed;
  }
  private isGroundInfantry(entity: Entity): boolean {
    return (
      entity.category === 'infantry' ||
      (entity.definitionId === 'eq_pegasus_assault' && Boolean(entity.landed))
    );
  }
  private isConnectedFriendlyPosition(owner: Faction, position: Point): boolean {
    const sector = sectors.find((entry) => distance(entry.point, position) < 240);
    const state = sector ? this.sectorState.get(sector.id) : undefined;
    return state?.owner === owner && state.connected[owner];
  }
  private destroy(entity: Entity): void {
    if (entity.occupants?.length) {
      for (const occupantId of entity.occupants) {
        const occupant = this.entities.get(occupantId);
        if (!occupant) continue;
        occupant.garrisonedIn = undefined;
        occupant.position = {
          x: entity.position.x + this.rng.range(-45, 45),
          y: entity.position.y + this.rng.range(-45, 45),
        };
        occupant.health -= occupant.maxHealth * 0.4;
        if (occupant.health <= 0) this.destroy(occupant);
      }
    }
    if (entity.kind === 'building' && entity.owner !== 'neutral' && entity.queue?.length) {
      for (const request of entity.queue) {
        const definition = getUnit(request.unitId);
        if (!definition) continue;
        this.players[entity.owner].supply += definition.cost * (request.active ? 0.5 : 1);
        if (request.reserved) {
          this.players[entity.owner].reservedCapacity -= definition.capacity;
          this.players[entity.owner].reservedEntities -= 1;
        }
      }
      this.message(`${entity.name} destroyed; its deployment queue was refunded.`);
    }
    if (entity.passengers?.length) {
      for (const passengerId of entity.passengers) {
        const passenger = this.entities.get(passengerId);
        if (!passenger) continue;
        passenger.embarkedIn = undefined;
        passenger.position = {
          x: entity.position.x + this.rng.range(-55, 55),
          y: entity.position.y + this.rng.range(-55, 55),
        };
        passenger.health -= passenger.maxHealth * (entity.air ? 0.55 : 0.3);
        if (passenger.health <= 0) this.destroy(passenger);
      }
    }
    this.entities.delete(entity.id);
    if (entity.kind === 'unit' && entity.owner !== 'neutral') {
      this.players[entity.owner].entities--;
      this.players[entity.owner].capacity -= entity.capacity;
    }
    if (entity.kind === 'unit' && entity.category === 'vehicle')
      this.createEntity({
        ...entity,
        kind: 'wreck',
        owner: 'neutral',
        name: `${entity.name} wreck`,
        health: 1,
        maxHealth: 1,
        speed: 0,
        damage: 0,
        capacity: 0,
        selected: false,
        order: 'Wreck',
        target: undefined,
        targetEntity: undefined,
        reload: 0,
        air: false,
        antiAir: false,
        destroyedOwner: entity.owner as Faction,
        position: { ...entity.position },
        previous: { ...entity.position },
      });
    this.message(`${entity.name} destroyed.`);
  }
  private recalculateConnections(): void {
    for (const faction of ['equestria', 'interdef'] as const) {
      const base = this.findSector(faction, 'base');
      const reached = new Set<string>();
      if (base) {
        const queue = [base.id];
        reached.add(base.id);
        while (queue.length) {
          const id = queue.shift();
          const definition = id ? sectors.find((entry) => entry.id === id) : undefined;
          if (!definition) continue;
          for (const next of definition.neighbors) {
            const state = this.sectorState.get(next);
            if (state?.owner === faction && !state.contested && !reached.has(next)) {
              reached.add(next);
              queue.push(next);
            }
          }
        }
      }
      for (const state of this.sectorState.values())
        state.connected[faction] = reached.has(state.id);
    }
  }
  private findHeadquarters(faction: Faction): Entity | undefined {
    return [...this.entities.values()].find(
      (entry) =>
        entry.owner === faction && entry.definitionId === hqId(faction) && entry.health > 0,
    );
  }
  private findSector(owner: Faction, type: 'base'): (typeof sectors)[number] | undefined {
    return sectors.find((sector) => sector.type === type && sector.initialOwner === owner);
  }
  private hasBuilding(faction: Faction, definitionId: string): boolean {
    return [...this.entities.values()].some(
      (entry) =>
        entry.owner === faction && entry.definitionId === definitionId && !entry.construction,
    );
  }
  private isRecoverableWreck(wreck: Entity): boolean {
    return new Set([
      'id_m113a3',
      'id_fennek',
      'id_m2a4_bradley',
      'id_m1134',
      'id_m1128',
      'eq_wc30',
      'eq_tc35_stalwart',
      'eq_lt32_peregrine',
    ]).has(wreck.definitionId);
  }
  private canRecoverBase(faction: Faction): boolean {
    const hasEngineer = [...this.entities.values()].some(
      (entry) =>
        entry.owner === faction &&
        entry.kind === 'unit' &&
        entry.health > 0 &&
        entry.definitionId.includes('engineer'),
    );
    if (hasEngineer) return true;
    return [...this.entities.values()].some((entry) => {
      if (
        entry.owner !== faction ||
        entry.kind !== 'building' ||
        entry.construction ||
        entry.health <= 0
      )
        return false;
      return (
        getBuilding(entry.definitionId)?.unlocks.some((unitId) => unitId.includes('engineer')) ??
        false
      );
    });
  }
  private hasPrerequisites(faction: Faction, prerequisiteIds: string[]): boolean {
    return prerequisiteIds.every((id) => id === hqId(faction) || this.hasBuilding(faction, id));
  }
  private clampPoint(point: Point): Point {
    return { x: clamp(point.x, 32, WORLD.width - 32), y: clamp(point.y, 32, WORLD.height - 32) };
  }
  private isClearLandingArea(unit: Entity, point: Point): boolean {
    if (point.x < 80 || point.x > WORLD.width - 80 || point.y < 80 || point.y > WORLD.height - 80)
      return false;
    return ![...this.entities.values()].some(
      (entity) =>
        entity.id !== unit.id &&
        !entity.embarkedIn &&
        distance(entity.position, point) <
          (entity.kind === 'building' || entity.wire || entity.mine || entity.kind === 'wreck'
            ? 92
            : 46),
    );
  }
  private transportSlots(definitionId: string): number {
    if (definitionId.includes('th28') || definitionId.includes('uh60')) return 2;
    if (
      definitionId.includes('wc30') ||
      definitionId.includes('m113') ||
      definitionId.includes('tc35') ||
      definitionId.includes('m2a4')
    )
      return 1;
    return 0;
  }
  private addEffect(
    owner: Faction,
    kind: AreaEffect['kind'],
    position: Point,
    radius: number,
    duration: number,
    damage: number,
    label: string,
  ): void {
    this.effects.set(this.nextEffect, {
      id: this.nextEffect++,
      owner,
      kind,
      position: this.clampPoint(position),
      radius,
      remaining: duration,
      damage,
      armed: false,
      label,
    });
  }
  private lineBlockedBySmoke(from: Point, to: Point): boolean {
    for (const effect of this.effects.values()) {
      if (effect.kind !== 'smoke') continue;
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const lengthSq = dx * dx + dy * dy;
      const t =
        lengthSq === 0
          ? 0
          : clamp(
              ((effect.position.x - from.x) * dx + (effect.position.y - from.y) * dy) / lengthSq,
              0,
              1,
            );
      const closest = { x: from.x + t * dx, y: from.y + t * dy };
      if (distance(closest, effect.position) <= effect.radius) return true;
    }
    return false;
  }
  private inHostileHeadwind(entity: Entity): boolean {
    return Boolean(
      this.isAirborne(entity) &&
      [...this.effects.values()].some(
        (effect) =>
          effect.kind === 'headwind' &&
          effect.owner !== entity.owner &&
          distance(effect.position, entity.position) <= effect.radius,
      ),
    );
  }
  private closestFriendly(owner: Faction, point: Point, radius: number): Entity | undefined {
    return [...this.entities.values()]
      .filter(
        (entity) =>
          entity.owner === owner &&
          entity.kind !== 'wreck' &&
          !entity.embarkedIn &&
          distance(entity.position, point) <= radius,
      )
      .sort((a, b) => distance(a.position, point) - distance(b.position, point))[0];
  }
  private setCooldown(entity: Entity, id: string, seconds: number): void {
    if (entity.cooldowns) entity.cooldowns[id] = seconds;
  }
  private slug(value: string): string {
    return value
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, '_')
      .replaceAll(/^_|_$/g, '');
  }
  private message(text: string): void {
    this.messages.push(`[${this.time.toFixed(0)}] ${text}`);
  }
  public hasUnlocked(faction: Faction, source: string): boolean {
    return source === hqId(faction) || this.hasBuilding(faction, source);
  }
}
